import { type Location } from "./location";

export enum OrderUpdateSource {
    USER_BOOKING = "user_booking",
    USER_CHECK_IN = "user_check_in",
    ADMIN = "admin"
}

export enum OrderDateSelectionType {
    IMMEDIATE = "immediate",
    LATER = "later",
    VOUCHER = "voucher"
}

export enum OrderTimePreference {
    MORNING = "morning",
    AFTERNOON = "afternoon"
}

export interface OrderStatus {
    /**
     * Whether the order has been paid
     */
    paid: boolean;
    /**
     * Whether the order has been cancelled
     */
    cancelled: boolean;
    /**
     * Whether the order has been refunded
     */
    refunded: boolean;
    /**
     * Whether the order has been completed
     */
    completed: boolean;
    /**
     * Whether the order has been rescheduled
     */
    rescheduled: boolean;
}

export interface OrderContactAddress {
    /**
     * Street
     */
    street: string;
    /**
     * House number
     */
    house_number: string;
    /**
     * House number addition, if applicable
     */
    house_number_addition?: string | null;
    /**
     * Postal code
     */
    postal_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Country
     */
    country: string;
}

export interface OrderVoucherInfo {
    /**
     * First name of the recipient
     */
    first_name: string;
    /**
     * Last name of the recipient
     */
    last_name: string;
    /**
     * Personal message for the voucher
     */
    message: string;
}

export interface OrderBasicContactInfo {
    /**
     * First name of the customer
     */
    first_name: string;
    /**
     * Last name of the customer
     */
    last_name: string;
    /**
     * Email address of the customer
     */
    email: string;
    /**
     * Phone number of the customer
     */
    phone: string;
}

export interface OrderContactInfo extends OrderBasicContactInfo {
    /**
     * Whether the customer is a business
     */
    is_business: boolean;
    /**
     * Name of the business, if applicable
     */
    business_name?: string | null;
    /**
     * Address of the customer
     */
    address: OrderContactAddress;
}

export interface OrderProduct {
    /**
     * Unique 36 character [0-9A-Z] identifier for the product
     */
    key: string;
    /**
     * Name of the product
     */
    name?: string | null;
    /**
     * Price of the product per person, in cents
     */
    price?: number | null;
}

export interface Order {
    /**
     * Unique 36 character [0-9A-Z] identifier for the order
     */
    key: string;
    /**
     * Payment status of the order
     */
    status: OrderStatus;
    /**
     * Creation date and time of the order
     */
    created_at: Date;
    /**
     * Last updated date and time of the order
     */
    updated_at: Date;
    /**
     * Source of the last update to the order
     */
    update_source: OrderUpdateSource;
    /**
     * Contact information of the customer
     */
    contact_info: OrderContactInfo;
    /**
     * Product associated with the order
     */
    product: OrderProduct;
    /**
     * Location associated with the order
     */
    location: Location;
    /**
     * Total amount of the order
     */
    amount: number;
    /**
     * Notes or comments associated with the order
     */
    notes?: string | null;
    /**
     * Type of date selection for the order
     */
    date_selection_type: OrderDateSelectionType;
    /**
     * Date of the order, if known
     */
    date?: Date | null;
    /**
     * Expiry override date for the order, if applicable
     */
    expiry_override_date?: Date | null;
    /**
     * Preferred time for the order, if known
     */
    time_preference?: OrderTimePreference | null;
    /**
     * Scheduled time for the order, if known
     */
    scheduled_time?: Date | null;
    /**
     * Scheduled time for the briefing, if applicable and known
     */
    briefing_scheduled_time?: Date | null;
    /**
     * Weights of the passengers, if known
     */
    weights?: number[] | null;
    /**
     * Emergency phone number, if known
     */
    emergency_phone?: string | null;
    /**
     * Voucher information as filled by the customer, if applicable
     */
    voucher_info?: OrderVoucherInfo | null;
    /**
     * Contact information for the voucher as filled by the voucher recipient, if applicable
     */
    voucher_contact_info?: OrderBasicContactInfo | null;
    /**
     * Mollie payment ID, if generated yet
     */
    payment_id?: string | null;
}
